/**
 * SlashCommandPlugin — type "/" at the start of a new block to open a
 * block-type picker, similar to Notion's "/" command.
 *
 * Uses LexicalTypeaheadMenuPlugin so arrow-key navigation, Enter to confirm,
 * and Escape to dismiss all work out of the box. Commands are sourced from the
 * shared BLOCK_COMMANDS so the slash menu and the drag "+" picker stay in sync.
 */
import { LexicalEditor, TextNode } from 'lexical';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BLOCK_COMMANDS, BlockCommand } from './blockCommands';
import classes from './SlashCommandPlugin.module.scss';

class SlashCommandOption extends MenuOption {
  command: BlockCommand;

  constructor(command: BlockCommand) {
    super(command.key);
    this.command = command;
  }
}

function filterCommands(commands: BlockCommand[], query: string): SlashCommandOption[] {
  const q = query.toLowerCase().trim();
  const matched = q
    ? commands.filter(
        (c) => c.label.toLowerCase().includes(q) || c.keywords.some((kw) => kw.includes(q)),
      )
    : commands;
  return matched.map((c) => new SlashCommandOption(c));
}

interface SlashMenuItemProps {
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: SlashCommandOption;
}

function SlashMenuItem({ isSelected, onClick, onMouseEnter, option }: SlashMenuItemProps) {
  return (
    <li
      className={`${classes.menuItem} ${isSelected ? classes.menuItemSelected : ''}`}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
    >
      <span className={classes.menuIcon}>{option.command.icon}</span>
      <span className={classes.menuContent}>
        <span className={classes.menuLabel}>{option.command.label}</span>
        <span className={classes.menuDescription}>{option.command.description}</span>
      </span>
    </li>
  );
}

export function SlashCommandPlugin({
  anchorElem,
  aiSlash,
}: {
  anchorElem: HTMLElement;
  aiSlash?: { available: boolean; onGenerate: () => void };
}) {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);

  const checkForSlashMatch = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0,
    maxLength: 25,
    allowWhitespace: false,
  });

  const options = useMemo(() => {
    // When GenAI is available, offer "Generate with AI" at the top of the menu.
    const aiCommand: BlockCommand | null = aiSlash?.available
      ? {
          key: 'ai-generate',
          label: 'Generate with AI',
          icon: '✨',
          description: 'Draft documentation for this resource',
          keywords: ['ai', 'generate', 'genai', 'gpt', 'draft', 'document'],
          run: () => aiSlash.onGenerate(),
        }
      : null;
    const commands = aiCommand ? [aiCommand, ...BLOCK_COMMANDS] : BLOCK_COMMANDS;
    return filterCommands(commands, queryString ?? '');
  }, [queryString, aiSlash]);

  const onSelectOption = useCallback(
    (selectedOption: SlashCommandOption, nodeToRemove: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        nodeToRemove?.remove();
      });
      selectedOption.command.run(editor);
      closeMenu();
    },
    [editor],
  );

  return (
    <LexicalTypeaheadMenuPlugin<SlashCommandOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForSlashMatch}
      options={options}
      preselectFirstItem={true}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) => {
        if (!anchorElementRef.current || options.length === 0) return null;
        const anchorRect = anchorElementRef.current.getBoundingClientRect();
        // Portal to body so position:fixed is in viewport space — the editor can
        // live inside a transformed side-panel, which would otherwise re-anchor it.
        return createPortal(
          <div
            data-testid="lexical-slash-menu"
            className={classes.menu}
            style={{ top: anchorRect.bottom + 4, left: anchorRect.left }}
          >
            <div className={classes.menuHeader}>
              <span>Blocks</span>
              <span className={classes.menuHint}>↑↓ navigate · Enter select · Esc close</span>
            </div>
            <ul className={classes.menuList} role="listbox">
              {options.map((option, i) => (
                <SlashMenuItem
                  key={option.key}
                  isSelected={selectedIndex === i}
                  onClick={() => {
                    setHighlightedIndex(i);
                    selectOptionAndCleanUp(option);
                  }}
                  onMouseEnter={() => setHighlightedIndex(i)}
                  option={option}
                />
              ))}
            </ul>
          </div>,
          document.body,
        );
      }}
    />
  );
}
